// Diagnostic warning catalog for the global Layer Properties console.
//
// Each finding is layer-KIND aware and carries a producer/converter-side
// recommended fix when the durable resolution is upstream (so operators don't
// paper over a producer bug with a session override). Pure + unit-tested.

import type { Layer } from '@geosoft/viewer-sdk';
import { isProjectedNonWebCrs, isWebFrameCrs } from './coordinates';
import { kindCanSwitchRenderFrame } from '@/engines/giro3d/displayCrsFramePolicy';
import type {
  LayerCoordinateDiagnostics,
  LayerRenderPathDiagnostics,
  LayerSourceDiagnostics,
  LayerWarning,
  LiveRenderDiagnostics,
} from './types';

export interface ComputeWarningsInput {
  readonly layer: Layer;
  readonly coordinates: LayerCoordinateDiagnostics;
  readonly source: LayerSourceDiagnostics;
  readonly renderPath: LayerRenderPathDiagnostics;
  readonly live?: LiveRenderDiagnostics | null;
}

const DEFAULT_WORLD_BBOX: readonly [number, number, number, number] = [-180, -85, 180, 85];

function bboxIsDefaultWorld(bbox: readonly number[] | undefined): boolean {
  if (!Array.isArray(bbox) || bbox.length < 4) return true;
  return DEFAULT_WORLD_BBOX.every((v, i) => Math.abs((bbox[i] as number) - v) < 1e-6);
}

function bboxCenterNearOrigin(bbox: readonly number[] | undefined): boolean {
  if (!Array.isArray(bbox) || bbox.length < 4) return false;
  const [minX, minY, maxX, maxY] = bbox as number[];
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  return Math.abs(cx) < 1e-6 && Math.abs(cy) < 1e-6;
}

/**
 * Compute all diagnostic warnings for a layer. Order is severity-stable
 * (errors first) so the panel can render them top-down.
 */
export function computeLayerWarnings(input: ComputeWarningsInput): LayerWarning[] {
  const { layer, coordinates, source, renderPath, live } = input;
  const warnings: LayerWarning[] = [];

  const isEnvironment = layer.kind === 'environment';
  const isMapOr3d = !isEnvironment && layer.kind !== 'timeseries';
  const canSwitchRenderFrame = kindCanSwitchRenderFrame(layer.kind);

  // --- Source / endpoints -------------------------------------------------
  if (isMapOr3d && source.endpoints.length === 0) {
    warnings.push({
      code: 'LP_W_NO_ENDPOINTS',
      severity: 'error',
      title: 'No source endpoints',
      detail: 'This layer has no resolvable endpoint URL, so no adapter can fetch its data.',
      recommendedFix:
        'Ensure the converter/scene item emits at least one endpoint (e.g. tiles, pmtiles, tileset3d, model, copc) for this artifact kind.',
      scope: 'converter',
    });
  }

  // --- Live render error --------------------------------------------------
  if (typeof live?.lastError === 'string' && live.lastError.trim().length > 0) {
    warnings.push({
      code: 'LP_W_RENDER_ERROR',
      severity: 'error',
      title: 'Engine reported a render error',
      detail: live.lastError,
      scope: 'viewer',
    });
  }

  // --- Coordinate / render-frame findings ---------------------------------
  const { sourceCrs, displayCrs, displayCrsProj4Present, alignmentStatus } = coordinates;

  if (isMapOr3d && canSwitchRenderFrame && isProjectedNonWebCrs(sourceCrs) && !displayCrs) {
    warnings.push({
      code: 'LP_W_MISSING_DISPLAY_CRS',
      severity: 'warning',
      title: 'Projected source CRS without a display CRS',
      detail: `Source CRS is ${sourceCrs} but no display CRS is set. The engine renders in EPSG:3857, so map, terrain, and 3D content may not line up.`,
      recommendedFix:
        'Have the converter stamp displayCrs + displayCrsProj4 on the project anchor / scene item so the viewer can switch the render frame.',
      scope: 'producer',
    });
  }

  if (canSwitchRenderFrame && displayCrs && displayCrs !== 'EPSG:3857' && !displayCrsProj4Present) {
    warnings.push({
      code: 'LP_W_DISPLAY_CRS_NO_PROJ4',
      severity: 'warning',
      title: 'Display CRS has no proj4 definition',
      detail: `Display CRS ${displayCrs} cannot be registered without a proj4 definition; the engine falls back to EPSG:3857.`,
      recommendedFix:
        'Provide displayCrsProj4 (the proj4 string for the display CRS) from the converter anchor or CRS catalog.',
      scope: 'producer',
    });
  }

  // Layer explicitly targets a different display CRS than the frame the viewer
  // is actually rendering in — the "camera/CRS confusion": geometry, basemap,
  // and 3D content can end up in different frames.
  if (
    canSwitchRenderFrame &&
    displayCrs &&
    coordinates.scopes.viewer.crs &&
    displayCrs !== coordinates.scopes.viewer.crs
  ) {
    warnings.push({
      code: 'LP_W_LAYER_FRAME_MISMATCH',
      severity: 'warning',
      title: 'Layer CRS differs from the viewer render frame',
      detail: `This layer targets ${displayCrs}, but the viewer is rendering in ${coordinates.scopes.viewer.crs}. Geometry may be mislocated until the whole scene shares one frame.`,
      recommendedFix:
        'Set the intended displayCrs (+ proj4) on the project anchor / first 3D layer so every layer renders in the same frame, or clear the per-layer displayCrs to render in the viewer frame.',
      scope: 'producer',
    });
  }

  if (!canSwitchRenderFrame && displayCrs && displayCrs !== 'EPSG:3857') {
    warnings.push({
      code: 'LP_W_DISPLAY_CRS_IGNORED_RASTER',
      severity: 'info',
      title: 'Display CRS is not used by this layer type',
      detail: `${layer.kind} tiles are served in EPSG:3857, so the viewer keeps the Web-Mercator render frame. A display CRS override will not move the pixels.`,
      recommendedFix:
        'Assign the correct source CRS only when the source coordinates are wrong or missing, and repair/reproject the raster or tile service upstream for a durable change.',
      scope: 'viewer',
    });
  }

  if (alignmentStatus === 'rejected') {
    warnings.push({
      code: 'LP_W_ALIGNMENT_REJECTED',
      severity: 'error',
      title: 'CRS alignment rejected',
      detail: 'The renderer rejected this layer\u2019s coordinate alignment, so it may be mislocated or hidden.',
      recommendedFix:
        'Confirm the source CRS and display CRS are supported and that a proj4 definition is available.',
      scope: 'converter',
    });
  }

  const needsAnchor =
    layer.worldFrame === 'planar-local' || sourceCrs === 'LOCAL';
  if (isMapOr3d && needsAnchor && !coordinates.anchor) {
    warnings.push({
      code: 'LP_W_MISSING_ANCHOR',
      severity: 'warning',
      title: 'Local frame without an origin anchor',
      detail: 'A local/planar-local frame needs an origin anchor (lat/lon/height) to place geometry on the globe.',
      recommendedFix:
        'Emit an origin anchor (lat, lon, height, heading) on the scene item / project anchor for local-frame data.',
      scope: 'producer',
    });
  }

  if (isMapOr3d && bboxIsDefaultWorld(layer.bbox)) {
    warnings.push({
      code: 'LP_W_DEFAULT_BBOX',
      severity: 'warning',
      title: 'Default world bbox in use',
      detail: 'The layer bbox is the default world extent, so fly-to framing and overlay alignment may be wrong.',
      recommendedFix:
        'Emit the true data bounds in the artifact manifest (bounds/bbox) so the viewer can frame and align the layer.',
      scope: 'converter',
    });
  }

  // 3D Tiles anchored at the origin (projected, non-ECEF) — the classic
  // "model flies to (0,0)" symptom.
  if (
    layer.kind === '3dtiles' &&
    isProjectedNonWebCrs(sourceCrs) &&
    sourceCrs !== 'EPSG:4978' &&
    !displayCrs &&
    bboxCenterNearOrigin(layer.bbox)
  ) {
    warnings.push({
      code: 'LP_W_3DTILES_ORIGIN_ANCHOR',
      severity: 'warning',
      title: '3D Tiles bbox centered at the origin',
      detail: 'A projected 3D Tiles bbox centered at (0,0) usually means the tileset is anchored at the frame origin instead of its real location.',
      recommendedFix:
        'Provide a real geographic bbox (lon/lat) and/or displayCrs so the tileset, basemap, and terrain share one frame.',
      scope: 'producer',
    });
  }

  // --- PMTiles vector source-layer ---------------------------------------
  if (layer.kind === 'vector' && layer.endpoints.pmtiles) {
    if (renderPath.renderPath === 'maplibre-overlay') {
      warnings.push({
        code: 'LP_W_PMTILES_MAPLIBRE_OVERLAY_APPROXIMATE',
        severity: 'warning',
        title: 'PMTiles uses the approximate MapLibre overlay path',
        detail:
          'This vector layer is rendered in a separate MapLibre canvas. It can preview/approximate 2D placement, but it does not share Giro3D terrain depth or true 3D orbit registration.',
        recommendedFix:
          'Use the Giro3D viewer/native PMTiles path for 3D-aligned feature layers; keep MapLibre for 2D, preview, or fallback diagnostics.',
        scope: 'viewer',
      });
    }

    const explicit =
      readStyleString(layer, 'sourceLayer') ?? readStyleString(layer, 'source_layer');
    const selected = source.selectedPmtilesSourceLayer ?? live?.selectedSourceLayer ?? null;

    // Declared a source-layer the archive does NOT contain (the classic
    // "vector toggles but is blank"): the engine reconciled to a different
    // layer. Higher severity than the "guessed" case because it points at a
    // concrete producer bug, even though the viewer recovered.
    const declared = live?.declaredSourceLayer ?? explicit ?? null;
    const available = live?.availableSourceLayers ?? null;
    const declaredMissing =
      declared != null &&
      Array.isArray(available) &&
      available.length > 0 &&
      !available.includes(declared);
    if (declaredMissing) {
      warnings.push({
        code: 'LP_W_PMTILES_SOURCE_LAYER_MISMATCH',
        severity: 'warning',
        title: 'Declared source-layer is not in the archive',
        detail:
          `The scene item declares source-layer "${declared}", but the PMTiles archive contains ` +
          `[${(available as string[]).join(', ')}]. The viewer reconciled to "${selected ?? '—'}" so features draw, ` +
          'but the declared name is wrong.',
        recommendedFix:
          `Stamp the archive's real source-layer name (e.g. "${selected ?? (available as string[])[0]}") on the scene item / converter output instead of "${declared}".`,
        scope: 'producer',
      });
    }

    if (!declaredMissing && !explicit && (selected === null || selected === 'features')) {
      warnings.push({
        code: 'LP_W_PMTILES_SOURCE_LAYER_GUESSED',
        severity: 'warning',
        title: 'PMTiles vector source-layer not explicit',
        detail:
          selected === 'features'
            ? 'The vector source-layer fell back to the default "features"; if the archive uses a different layer name, no features will draw.'
            : 'No explicit vector source-layer is set; it will be guessed from archive metadata.',
        recommendedFix:
          'Emit PMTiles metadata with vector_layers, or set style.sourceLayer on the scene item, so the overlay binds to the correct vector layer.',
        scope: 'producer',
      });
    }
  }

  // --- Point cloud specific --------------------------------------------------
  if (layer.kind === 'pointcloud') {
    const style = (layer.style ?? {}) as Record<string, unknown>;
    const pc = (typeof style['pointcloud'] === 'object' && style['pointcloud'] !== null
      ? style['pointcloud'] : {}) as Record<string, unknown>;
    const coordMode = pc['coordinateMode'];
    if (coordMode === 'local-engineering') {
      warnings.push({
        code: 'LP_W_POINTCLOUD_LOCAL_FRAME',
        severity: 'warning',
        title: 'Point cloud in local engineering frame',
        detail: 'This point cloud has no real-world CRS. Basemap/terrain alignment and geographic measurements are unavailable.',
        recommendedFix:
          'Register the point cloud with a CRS before conversion, or provide a manual origin anchor.',
        scope: 'producer',
      });
    }
    if (!pc['pointCount'] && !source.featureOrObjectCount) {
      warnings.push({
        code: 'LP_W_POINTCLOUD_NO_POINT_COUNT',
        severity: 'warning',
        title: 'Point count not reported',
        detail: 'The converter did not emit a point count. Budget estimation and the quality panel may show incomplete statistics.',
        recommendedFix:
          'Ensure the converter profiler (PDAL info) completes successfully and stamps pointCount on the scene item.',
        scope: 'converter',
      });
    }
    if (
      !layer.endpoints.copc &&
      !layer.endpoints.potree &&
      !layer.endpoints.tileset3d &&
      !layer.endpoints.pointcloud
    ) {
      warnings.push({
        code: 'LP_W_POINTCLOUD_NO_ENDPOINT',
        severity: 'error',
        title: 'No point cloud endpoint',
        detail: 'This layer is kind=pointcloud but has no copc, potree, tileset3d, or pointcloud endpoint. Nothing will render.',
        recommendedFix:
          'Ensure the converter emits copc or potree endpoint URLs for point cloud artifacts.',
        scope: 'converter',
      });
    }
  }

  // --- Basemap CRS incompatibility (ADR-023) --------------------------------
  // A basemap is incompatible when its CRS differs from the viewer frame and
  // Giro3D cannot reproject: Web Mercator in a projected frame, or a projected
  // basemap whose CRS differs from the projected viewer frame.
  const isBasemap = readStyleString(layer, 'gtwinSlot') === 'basemaps'
    || readStyleString(layer, 'family') === 'basemap'
    || readStyleString(layer, 'family') === 'imagery';
  const basemapCrs = (layer.crs ?? 'EPSG:3857').toUpperCase();
  const viewerCrs = coordinates.scopes.viewer.crs?.toUpperCase();
  if (
    isBasemap
    && viewerCrs
    && !isWebFrameCrs(viewerCrs)
    && basemapCrs !== viewerCrs
  ) {
    const isWebMerc = isWebFrameCrs(basemapCrs);
    warnings.push({
      code: 'LP_W_BASEMAP_CRS_INCOMPATIBLE',
      severity: 'warning',
      title: 'Basemap suppressed: CRS incompatible',
      detail: isWebMerc
        ? `This basemap uses ${basemapCrs} but the viewer frame is ` +
          `${viewerCrs}. Web Mercator basemaps are hidden in projected ` +
          `frames to prevent misalignment.`
        : `This basemap uses ${basemapCrs} but the viewer frame is ` +
          `${viewerCrs}. Basemaps in a different projected CRS are ` +
          `suppressed because Giro3D does not reproject raster tiles ` +
          `between arbitrary projected coordinate systems.`,
      recommendedFix:
        `Provide a basemap in ${viewerCrs}, or switch the scene to a Web Mercator frame.`,
      scope: 'producer',
    });
  }

  // --- Store vs engine visibility desync (the "toggles but invisible" case) -
  if (renderPath.visibilityInSync === false) {
    if (renderPath.storeVisible && renderPath.mounted === false) {
      warnings.push({
        code: 'LP_W_VISIBLE_NOT_MOUNTED',
        severity: 'warning',
        title: 'Marked visible but not mounted',
        detail: 'The layer is toggled on in the scene tree but is not mounted in any engine, so nothing renders.',
        scope: 'viewer',
      });
    } else if (renderPath.storeVisible && renderPath.engineVisible === false) {
      warnings.push({
        code: 'LP_W_VISIBILITY_DESYNC',
        severity: 'warning',
        title: 'Visibility out of sync with the engine',
        detail: 'The layer is toggled on but the engine reports it hidden — a likely cause of "toggles but does not render".',
        scope: 'viewer',
      });
    }
  }

  return warnings;
}

function readStyleString(layer: Layer, key: string): string | null {
  const value = (layer.style as Record<string, unknown> | undefined)?.[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

// Re-export for callers that only need the web-frame predicate.
export { isWebFrameCrs };
